import React, { useContext, useEffect, useState } from 'react';
import { getUsersIndex, inviteUser, updateUser, deleteUser } from './api';
import { useTranslation } from 'react-i18next';
import { AuthenticationContext } from './AuthenticationProvider';
import { User } from './types/User';
import { UserPolicy } from './types/UserPolicy';
import { hasSystemPolicy } from '../utility';
import { trackPromise } from 'react-promise-tracker';

export interface IUsersContext {
  users: UserData[];

  onCreateUser(_values: UserData): void;
  onUpdateUser(_values: UserData): void;
  onDeleteUser(_id: number): void;
}

export type CreateUser = {
  id?: number;
  first_name: string;
  last_name: string;
  email: string;
  system_admin?: boolean;
  project_admin?: boolean;
  user_admin?: boolean;
};

export interface IUsersProviderProps {
  children: any;
}

const UsersContext = React.createContext<IUsersContext>({
  users: [],

  onCreateUser(_values: UserData) {},
  onUpdateUser(_values: UserData) {},
  onDeleteUser(_id: number) {},
});

export type UserData = {
  id?: number;
  firstName: string;
  lastName: string;
  email: string;
  systemAdmin: boolean;
  projectAdmin: boolean;
  userAdmin: boolean;
};

const createUserData = (
  users: User[]
) => {
  let arr: UserData[] = [];
  users.forEach((user) => {
    arr.push({
      id: user.id,
      firstName: user.first_name,
      lastName: user.last_name,
      email: user.email,
      systemAdmin: hasSystemPolicy(
        'FCC_Admin',
        user.user_policies as UserPolicy[]
      ),
      projectAdmin: hasSystemPolicy(
        'FCC_CanManageProjects',
        user.user_policies as UserPolicy[]
      ),
      userAdmin: hasSystemPolicy(
        'FCC_CanManageUsers',
        user.user_policies as UserPolicy[]
      ),
    });
  });

  return arr;
};

const UsersProvider = (props: IUsersProviderProps) => {
  const { children } = props;

  const [users, setUsers] = useState<UserData[]>([]);
  const { i18n } = useTranslation();

  const { isAuthenticated, token } = useContext(AuthenticationContext);

  useEffect(() => {
    if (isAuthenticated() && token) {
      trackPromise(getUsersIndex(token)).then((data) => {
        if (data) {
          setUsers(createUserData(data.users));
        }
      });
    }
  }, [isAuthenticated, i18n, token]);

  const onCreateUser = (values: UserData) => {
    trackPromise(inviteUser(
      {
        first_name: values.firstName,
        last_name: values.lastName,
        email: values.email,
        system_admin: values.systemAdmin,
        project_admin: values.projectAdmin,
        user_admin: values.userAdmin,
      },
      token as string
    ))
      .then(() => {
        trackPromise(getUsersIndex(token as string))
          .then((data) => {
            if (data) {
              setUsers(createUserData(data.users));
            }
          })
          .catch((err) => console.log(err));
      })
      .catch((err) => console.log(err));
  };

  const onUpdateUser = (values: UserData) => {
    trackPromise(updateUser({
      id: values.id,
      first_name: values.firstName,
      last_name: values.lastName,
      email: values.email,
      system_admin: values.systemAdmin,
      project_admin: values.projectAdmin,
      user_admin: values.userAdmin,
    }, token as string))
      .then(() => {
        trackPromise(getUsersIndex(token as string))
          .then((data) => {
            if (data) {
              setUsers(createUserData(data.users));
            }
          })
          .catch((err) => console.log(err));
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const onDeleteUser = (id: number) => {
    trackPromise(deleteUser(id, token as string))
      .then(() => {
        getUsersIndex(token as string)
          .then((data) => {
            if (data) {
              setUsers(createUserData(data.users));
            }
          })
          .catch((err) => console.log(err));
      })
      .catch((err) => {
        console.log(err);
      });
  };

  return (
    <UsersContext.Provider
      value={{
        users,

        onCreateUser,
        onUpdateUser,
        onDeleteUser,
      }}
    >
      {children}
    </UsersContext.Provider>
  );
};

export { UsersContext, UsersProvider };
